import React, { useState, useEffect } from 'react';
import { X, QrCode } from 'lucide-react';
import * as OTPAuth from 'otpauth';
import { Html5QrcodeScanner, Html5QrcodeSupportedFormats } from 'html5-qrcode';
import DeliverySelect from './DeliverySelect';
import { useTheme } from '../ThemeContext';

const ModalSalesforce = ({ isOpen, onClose, onAdd, deliveries, logAction }) => {
    const [formData, setFormData] = useState({
        name: '',
        username: '',
        description: '',
        secretKey: '',
        delivery: ''
    });
    const [error, setError] = useState('');
    const [showScanner, setShowScanner] = useState(false);
    const { isDark } = useTheme();

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    useEffect(() => {
        if (showScanner) {
            const scanner = new Html5QrcodeScanner("reader", {
                qrbox: {
                    width: 250,
                    height: 250,
                },
                fps: 5,
                // Ajout des textes personnalisés
                rememberLastUsedCamera: true,
                aspectRatio: 1.0,
                showTorchButtonIfSupported: true,
                // Personnalisation des textes
                formatsToSupport: [Html5QrcodeSupportedFormats.QR_CODE],
                // Messages en français
                text: {
                    camera: "Caméra",
                    chooseImage: "Choisir une image",
                    drag: "ou déposez une image ici",
                    file: "Parcourir",
                    scan: "Scanner",
                    scanning: "Scan en cours...",
                    torchOff: "Éteindre le flash",
                    torchOn: "Allumer le flash",
                    uploadImage: "Télécharger une image",
                    requestingCamera: "Demande d'accès à la caméra...",
                    scanningInProgress: "Scan en cours...",
                    permissionDenied: "Permission refusée",
                    permissionRequest: "Demande de permission",
                    noCamera: "Aucune caméra trouvée",
                    retry: "Réessayer",
                    scanImage: "Scanner une image",
                    scanAnImageFile: "Scanner à partir d'un fichier",
                    requestCameraPermissions: "Autoriser l'accès à la caméra",
                    chooseAnImage: "Choisir une image",
                    noImageChosen: "Aucune image sélectionnée",
                    dropImageHere: "Déposez une image ici",
                    scanUsingCamera: "Scanner avec la caméra",
                    selectOrDropImage: "Sélectionnez ou déposez une image",
                    cameraPermissionRequesting: "Demande d'accès à la caméra..."
                },
                // Style personnalisé
                buttonClassName: "px-4 py-2 bg-blue-500 text-white rounded-xl hover:bg-blue-600 transition-colors duration-200",
                containerClassName: "rounded-xl overflow-hidden",
                fileSelectionUiClassName: "space-y-4"
            });

            scanner.render(
                (decodedText) => {
                    try {
                        // Si c'est une URL otpauth:// (Google, Microsoft, etc.)
                        if (decodedText.startsWith('otpauth://')) {
                            const url = new URL(decodedText);
                            const secret = url.searchParams.get('secret');
                            if (secret) {
                                setFormData(prev => ({
                                    ...prev,
                                    secretKey: secret
                                }));
                                scanner.clear();
                                setShowScanner(false);
                                return;
                            }
                        }

                        // Si c'est déjà une clé secrète brute (certains services)
                        if (/^[A-Z2-7]+=*$/i.test(decodedText)) {
                            setFormData(prev => ({
                                ...prev,
                                secretKey: decodedText
                            }));
                            scanner.clear();
                            setShowScanner(false);
                            return;
                        }

                        // Si c'est au format JSON (certains services personnalisés)
                        try {
                            const jsonData = JSON.parse(decodedText);
                            if (jsonData.secret) {
                                setFormData(prev => ({
                                    ...prev,
                                    secretKey: jsonData.secret
                                }));
                                scanner.clear();
                                setShowScanner(false);
                                return;
                            }
                        } catch (e) {
                            // Ce n'est pas du JSON, on continue
                        }

                        // Si on arrive ici, le format n'est pas reconnu
                        setError('Format de QR code non supporté');
                        scanner.clear();
                        setShowScanner(false);

                    } catch (error) {
                        console.error('Erreur de parsing du QR code:', error);
                        setError('Format de QR code non reconnu');
                        scanner.clear();
                        setShowScanner(false);
                    }
                },
                (error) => {
                    console.warn(error);
                }
            );

            return () => {
                scanner.clear();
            };
        }
    }, [showScanner]);

    const handleSubmit = async () => {
        try {
            const totp = new OTPAuth.TOTP({
                secret: formData.secretKey,
                algorithm: 'SHA1',
                digits: 6,
                period: 30
            });

            const code = totp.generate();
            if (code) {
                const response = await fetch('/api/salesforce', {
                    method: 'POST',
                    credentials: 'include',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        name: formData.name,
                        username: formData.username,
                        description: formData.description,
                        secret: formData.secretKey,
                        delivery: formData.delivery,
                        sf_username: formData.username,
                        sf_description: formData.description
                    })
                });

                if (!response.ok) {
                    throw new Error('Erreur lors de la sauvegarde');
                }

                const savedService = await response.json();

                // Logger la création du service
                if (logAction) {
                    logAction('SERVICE_ADDED', {
                        serviceName: formData.name,
                        serviceId: savedService.id,
                        delivery: formData.delivery,
                        username: formData.username
                    });
                }

                onAdd({
                    ...savedService,
                    id: 'salesforce-' + savedService.id
                });

                // Réinitialiser le formulaire
                setFormData({
                    name: '',
                    username: '',
                    description: '',
                    secretKey: '',
                    delivery: ''
                });
                setError('');

                onClose();
            }
        } catch (error) {
            console.error('Erreur lors de l\'ajout:', error);
            setError('Erreur lors de l\'ajout du service. Vérifiez vos informations.');
        }
    };

    // Composant du scanner QR Code
    const renderScanner = () => (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50">
            <div className="bg-white dark:bg-gray-800 p-6 rounded-2xl max-w-md w-full m-4 relative space-y-4">
                <button
                    onClick={() => setShowScanner(false)}
                    className="absolute right-4 top-4 p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                    <X size={20} />
                </button>

                <div className="mb-4">
                    <h3 className="text-xl font-bold text-gray-800 dark:text-white">Scanner un QR Code</h3>
                    <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">
                        Positionnez le QR code devant votre caméra ou sélectionnez une image
                    </p>
                </div>

                <div id="reader" className="overflow-hidden rounded-xl shadow-inner bg-gray-50">
                    {/* Le scanner sera injecté ici */}
                </div>
            </div>
        </div>
    );

    // Réinitialiser le formulaire quand la modal se ferme
    useEffect(() => {
        if (!isOpen) {
            setFormData({
                name: '',
                username: '',
                description: '',
                secretKey: '',
                delivery: ''
            });
            setError('');
        }
    }, [isOpen]);

    if (!isOpen) return null;

    return (
        <>
            <div className="fixed inset-0 z-50">
                <div className="fixed inset-0 bg-black/50" onClick={onClose} />
                <div className="fixed inset-0 z-50 flex items-start md:items-center justify-center md:p-4">
                    <div className="relative bg-white dark:bg-gray-800 rounded-2xl md:rounded-2xl p-6 w-full max-w-md space-y-6 md:h-auto h-screen md:max-h-[85vh] overflow-y-auto">
                        <button
                            onClick={onClose}
                            className="absolute right-4 top-4 p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
                        >
                            <X size={20} />
                        </button>

                        <div className="text-center pt-2">
                            <h2 className="text-2xl font-bold text-gray-800 dark:text-white">Ajouter un service</h2>
                            <p className="text-gray-600 dark:text-gray-400 mt-2">
                                Configurez votre authentification
                            </p>
                        </div>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Nom du service *
                                </label>
                                <input
                                    type="text"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleChange}
                                    placeholder="Ex: Salesforce Prod"
                                    className="w-full px-4 py-3 rounded-xl border focus:ring-2 focus:ring-blue-400 focus:border-transparent outline-none dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                                    required
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Nom d'utilisateur *
                                </label>
                                <input
                                    type="text"
                                    name="username"
                                    value={formData.username}
                                    onChange={handleChange}
                                    placeholder="Ex: john.doe@solganeo.com"
                                    className="w-full px-4 py-3 rounded-xl border focus:ring-2 focus:ring-blue-400 focus:border-transparent outline-none dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                                    required
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Description
                                </label>
                                <input
                                    type="text"
                                    name="description"
                                    value={formData.description}
                                    onChange={handleChange}
                                    placeholder="Ex: Environnement de production"
                                    className="w-full px-4 py-3 rounded-xl border focus:ring-2 focus:ring-blue-400 focus:border-transparent outline-none dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                                />
                            </div>

                            <div>
                                <DeliverySelect
                                    value={formData.delivery}
                                    onChange={handleChange}
                                    deliveries={deliveries}
                                />
                            </div>

                            <div className="relative">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Clé secrète *
                                </label>
                                <input
                                    type="text"
                                    name="secretKey"
                                    value={formData.secretKey}
                                    onChange={handleChange}
                                    placeholder="Entrez la clé secrète ou scannez le QR code"
                                    className="w-full px-4 py-3 pr-12 rounded-xl border focus:ring-2 focus:ring-blue-400 focus:border-transparent outline-none dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                                    required
                                />
                                <button
                                    onClick={() => setShowScanner(true)}
                                    type="button"
                                    className="absolute right-2 top-9 p-2 bg-blue-50 dark:bg-blue-400/10 hover:bg-blue-100 dark:hover:bg-blue-400/20 rounded-lg transition-colors"
                                    title="Scanner un QR Code"
                                >
                                    <QrCode size={20} className="text-blue-500" />
                                </button>
                            </div>

                            {error && (
                                <div className="text-red-500 dark:text-red-400 text-sm bg-red-50 dark:bg-red-900/30 p-3 rounded-lg">
                                    {error}
                                </div>
                            )}
                        </div>

                        <div className="flex gap-4 pt-2">
                            <button
                                onClick={onClose}
                                className="flex-1 px-4 py-3 rounded-xl border hover:bg-gray-50 dark:hover:bg-gray-700 dark:border-gray-700 dark:text-white transition-colors duration-200"
                            >
                                Annuler
                            </button>
                            <button
                                onClick={handleSubmit}
                                disabled={!formData.name || !formData.username || !formData.secretKey}
                                className="flex-1 px-4 py-3 rounded-xl bg-blue-500 text-white hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors duration-200"
                            >
                                Ajouter
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {showScanner && renderScanner()}
        </>
    );
};

export default ModalSalesforce;